import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Briefcase, Building2, ExternalLink, User, type LucideIcon } from "lucide-react";
import { Badge, Card } from "@/design-system/components/ui";
import { businessesApi, entitiesApi, personsApi } from "@/api/counterparties";
import { brokersApi } from "@/api/brokers";
import { usePartyDetail } from "@/hooks/parties/useParties";
import { partyBasePath, type PartyType } from "@/types/parties";
import { PortalAccountLink } from "@/components/crosslinks/BrokerCrossLinks";
import { MaskedSensitiveField } from "@/components/counterparties/MaskedSensitiveField";
import { displayEmail, NO_EMAIL_ON_FILE } from "@/design-system/lib/format";

type Role = "Broker" | "Borrower" | "Seller" | "Business";

interface BaseProps {
  role: Role;
  className?: string;
}

interface PersonCardProps extends BaseProps {
  kind: "Person";
  personId: string;
}
interface EntityCardProps extends BaseProps {
  kind: "Entity";
  entityId: string;
}
interface BusinessCardProps extends BaseProps {
  kind: "Business";
  businessId: string;
}
interface PartyCardProps extends BaseProps {
  kind: "Party";
  partyType: PartyType;
  partyId: string;
}
interface BrokerCardProps extends BaseProps {
  kind: "Broker";
  brokerId: string;
}
interface EmptyCardProps extends BaseProps {
  kind: "Empty";
  emptyHint?: string;
}

type Props =
  | PersonCardProps
  | EntityCardProps
  | BusinessCardProps
  | PartyCardProps
  | BrokerCardProps
  | EmptyCardProps;

const ROLE_ICON: Record<Role, LucideIcon> = {
  Broker: Briefcase,
  Borrower: User,
  Seller: User,
  Business: Building2,
};

export function CounterpartyCard(props: Props) {
  const Icon = ROLE_ICON[props.role];

  return (
    <Card className="flex flex-col gap-space-2 min-h-[120px]">
      <div className="flex items-center gap-space-2">
        <Icon size={14} aria-hidden />
        <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow">
          {props.role}
        </div>
      </div>

      {props.kind === "Empty" ? (
        <p className="text-small text-gray-mid italic m-0">
          {props.emptyHint ?? `No ${props.role.toLowerCase()} attached.`}
        </p>
      ) : props.kind === "Person" ? (
        <PersonCardBody personId={props.personId} role={props.role} />
      ) : props.kind === "Entity" ? (
        <EntityCardBody entityId={props.entityId} role={props.role} />
      ) : props.kind === "Business" ? (
        <BusinessCardBody businessId={props.businessId} />
      ) : props.kind === "Party" ? (
        <PartyCardBody partyType={props.partyType} partyId={props.partyId} role={props.role} />
      ) : (
        <BrokerCardBody brokerId={props.brokerId} />
      )}
    </Card>
  );
}

function PersonCardBody({ personId, role }: { personId: string; role: Role }) {
  const q = useQuery({
    queryKey: ["counterparties", "persons", "detail", personId],
    queryFn: () => personsApi.get(personId),
  });
  if (q.isLoading) return <Skeleton />;
  if (!q.data) return <ErrorBody message="Could not load person." />;
  const p = q.data;
  return (
    <>
      <div className="flex items-center justify-between">
        <span className="font-semibold text-small text-onyx truncate">{p.displayName}</span>
        <Badge status="info">Person</Badge>
      </div>
      <MaskedSensitiveField label="SSN" masked={p.ssnMasked} />
      <p className="text-caption text-gray-mid m-0 truncate">
        {displayEmail(p.primaryEmail) ??
          (p.primaryEmail ? NO_EMAIL_ON_FILE : "—")}{" "}
        · {p.primaryPhone}
      </p>
      <ViewLink to={`/counterparties/persons/${p.id}`} role={role} />
    </>
  );
}

function EntityCardBody({ entityId, role }: { entityId: string; role: Role }) {
  const q = useQuery({
    queryKey: ["counterparties", "entities", "detail", entityId],
    queryFn: () => entitiesApi.get(entityId),
  });
  if (q.isLoading) return <Skeleton />;
  if (!q.data) return <ErrorBody message="Could not load entity." />;
  const e = q.data;
  return (
    <>
      <div className="flex items-center justify-between">
        <span className="font-semibold text-small text-onyx truncate">{e.legalEntityName}</span>
        <Badge status="info">Entity · {e.entityType}</Badge>
      </div>
      <MaskedSensitiveField label="EIN" masked={e.einMasked} />
      <p className="text-caption text-gray-mid m-0 truncate">
        {displayEmail(e.primaryEmail) ??
          (e.primaryEmail ? NO_EMAIL_ON_FILE : "—")}
      </p>
      <ViewLink to={`/counterparties/entities/${e.id}`} role={role} />
    </>
  );
}

function BusinessCardBody({ businessId }: { businessId: string }) {
  const q = useQuery({
    queryKey: ["counterparties", "businesses", "detail", businessId],
    queryFn: () => businessesApi.get(businessId),
  });
  if (q.isLoading) return <Skeleton />;
  if (!q.data) return <ErrorBody message="Could not load business." />;
  const b = q.data;
  return (
    <>
      <div className="flex items-center justify-between">
        <span className="font-semibold text-small text-onyx truncate">{b.legalEntityName}</span>
        <Badge status="info">{b.naicsCode}</Badge>
      </div>
      <MaskedSensitiveField label="EIN" masked={b.einMasked} />
      <p className="text-caption text-gray-mid m-0 truncate">{b.industryDescription}</p>
      <ViewLink to={`/counterparties/businesses/${b.id}`} role="Business" />
    </>
  );
}

// Counterparty sunset — a deal's Borrower/Seller is a Party FK; resolve the
// display facts from the party registry and link to its detail page.
function PartyCardBody({
  partyType,
  partyId,
  role,
}: {
  partyType: PartyType;
  partyId: string;
  role: Role;
}) {
  const q = usePartyDetail(partyType, partyId);
  if (q.isLoading) return <Skeleton />;
  if (!q.data) return <ErrorBody message={`Could not load ${partyType.toLowerCase()}.`} />;
  const p = q.data;
  return (
    <>
      <div className="flex items-center justify-between">
        <span className="font-semibold text-small text-onyx truncate">{p.displayName}</span>
        <Badge status="info">{p.partyKind}</Badge>
      </div>
      <p className="text-caption text-gray-mid m-0 truncate">
        {displayEmail(p.email) ?? (p.email ? NO_EMAIL_ON_FILE : "—")}
        {p.phone ? ` · ${p.phone}` : ""}
      </p>
      <ViewLink to={`${partyBasePath(partyType)}/${p.id}`} role={role} />
    </>
  );
}

function BrokerCardBody({ brokerId }: { brokerId: string }) {
  const q = useQuery({
    queryKey: ["brokers", "detail", brokerId],
    queryFn: () => brokersApi.get(brokerId),
  });
  // Broker ↔ External User cross-link — surface the broker's portal account
  // alongside the CRM record so the deal reaches both.
  const portal = useQuery({
    queryKey: ["brokers", "invite-status", brokerId],
    queryFn: () => brokersApi.inviteStatus(brokerId),
  });
  if (q.isLoading) return <Skeleton />;
  if (!q.data) return <ErrorBody message="Could not load broker." />;
  const b = q.data;
  return (
    <>
      <span className="font-semibold text-small text-onyx truncate">{b.companyName}</span>
      <p className="text-caption text-gray-mid m-0 truncate">{b.name ?? "—"}</p>
      <ViewLink to={`/brokers/${b.id}`} role="Broker" />
      <PortalAccountLink
        portalUserId={portal.data?.collectionAccountId ?? null}
        className="inline-flex items-center gap-space-1 text-caption uppercase tracking-wide text-gold-shadow hover:underline"
      />
    </>
  );
}

function ViewLink({ to, role }: { to: string; role: Role }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-space-1 text-caption uppercase tracking-wide text-gold-shadow hover:underline mt-auto"
    >
      View {role.toLowerCase()} <ExternalLink size={10} aria-hidden />
    </Link>
  );
}

function Skeleton() {
  return <p className="text-small text-gray-mid italic m-0">Loading…</p>;
}

function ErrorBody({ message }: { message: string }) {
  return <p className="text-small text-status-error m-0">{message}</p>;
}
